/*
 * Carbisdale - Context Menu
 *
 * Kontextmenue (Rechtsklick) fuer Formatierung, Clipboard und Strukturoperationen.
 * Erscheint in allen Eingabebereichen (Main, Outline, Context, Core Console).
 * Eigenes Modul — unabhaengig vom bestehenden Menu-System (Command Palette).
 *
 * Teilt sich den Overlay mit Menu und LinkPicker (nie gleichzeitig offen).
 *
 * Layout-Modell (siehe context-menu-redesign-2026-05-14.md):
 *  - Jede Cell besteht aus Gutter (2 Cells in Mono) + Label an der
 *    Fluchtlinie der Spalte. Items ohne Praefix lassen den Gutter leer;
 *    das Label startet trotzdem an der Fluchtlinie.
 *  - Spalten haben **variable** Breiten — pro Spalte wird das laengste
 *    Label gemessen + Gutter + Trailing-Padding addiert.
 *  - Spezialfall List: Praefix `1.` ist 2 Cells breit (kein Space dazwischen).
 *  - Grid-Architektur ist 5-spaltig. Spalten ohne Items haben Breite 0
 *    und werden nicht gerendert — Plain/Code/Lock/@Link fuellen die
 *    5. Spalte bzw. neue Positionen erst in Schritt 6.
 */

import { Style } from './config.js'
import { computeStringLayout } from './GlyphMetrics.js'
import { blurKeyboardSink } from './touchKeyboard.js'

// === Deklarative Menuestruktur ===
//
// Pro Item:
//   col            — Spalte 0..MAX_COLS-1 im Grid
//   prefix?        — Glyphe im Gutter (z.B. '#', '←'). Fehlt → leerer Gutter.
//   prefixCompact? — true: Praefix ist 2 Chars breit, kein Space dahinter
//                    (z.B. `1.` → `1.List`). Default false (`# Head`).
//   label          — Wort an der Fluchtlinie
//   action         — Action-ID (siehe contextMenuActions.js)
//   previewStyle?  — Style-Override fuer Preview-Rendering (Bold/Italic/...)

const MENU_GROUPS = [
    {
        id: 'navigation',
        items: [
            { col: 0, prefix: '←', label: 'Back', action: 'back' },
            { col: 1, prefix: '→', label: 'Fwd', action: 'forward' },
            // @Link: oeffnet den LinkPicker programmatisch (kein '@' im Text).
            // Item-Level-Sichtbarkeit: nur text/note/info. In separator
            // ergibt der Link keinen Sinn (Separator selbst hat keinen Text-
            // Inhalt), in console/memory/personal_dict ist navigation eh
            // nicht sichtbar.
            { col: 2, prefix: '@', label: 'Link', action: 'link', visibleIn: ['text', 'note', 'info'] }
        ]
    },
    {
        id: 'blockFormats',
        items: [
            // Head: bold-Preview (Headings sind fett), Quote: italic-Preview
            // (Blockquotes klassisch kursiv). Bullet/List zeigen ihr Format
            // bereits ueber das Praefix-Glyph (• bzw. 1.) — kein zusaetzlicher
            // Style noetig. Plain togglet `para.codeBlock` (raw, mono, kein
            // AutoCorrect) — Preview als Monospace.
            { col: 0, prefix: '#', label: 'Head', action: 'heading', previewStyle: Style.MENU_ITEM_BOLD },
            { col: 1, prefix: '•', label: 'Bullet', action: 'bullet' },
            { col: 2, prefix: '1.', prefixCompact: true, label: 'List', action: 'numbered' },
            { col: 3, prefix: '>', label: 'Quote', action: 'blockquote', previewStyle: Style.MENU_ITEM_ITALIC },
            // 'Raw' (statt 'Plain'): monospaced-Laufweite von 'Plain' war im
            // Prop-Mode zu breit — 'Raw' ist 3 statt 5 Zeichen.
            { col: 4, label: 'Raw', action: 'raw', previewStyle: Style.MENU_ITEM_CODE }
        ]
    },
    {
        id: 'structure',
        items: [
            // Mono: Text-Praefix (= - +). Prop: Circular-Icon mit demselben
            // Symbol. iconSymbol wird im Prop-Pfad anstelle des Text-Praefixes
            // gerendert. Lock: Icon 'v' nur im Prop, in Mono kein Praefix
            // (Boris explizit) — daher `prefix` weggelassen.
            // Reihenfolge: Break/Lock (haeufiger) vorne, Fold/Unfold hinten.
            { col: 0, prefix: '=', iconSymbol: '=', label: 'Break', action: 'break' },
            { col: 1, iconSymbol: 'v', label: 'Lock', action: 'lock' },
            { col: 2, prefix: '-', iconSymbol: '-', label: 'Fold', action: 'fold' },
            { col: 3, prefix: '+', iconSymbol: '+', label: 'Unfold', action: 'unfold' }
        ]
    },
    {
        id: 'indentMove',
        items: [
            // Out/In: Mono-Praefix '<'/'>', Prop-Icon mit '<'/'>' (kein
            // '←'/'→' Icon im _drawControlSymbol vorhanden — die Chevrons
            // sind das proportionale Pendant). Up/Down behalten Text-Pfeile
            // (kein passendes Icon-Symbol).
            { col: 0, prefix: '<', iconSymbol: '<', label: 'Out', action: 'outdent' },
            { col: 1, prefix: '>', iconSymbol: '>', label: 'In', action: 'indent' },
            { col: 2, prefix: '↑', label: 'Up', action: 'moveUp' },
            { col: 3, prefix: '↓', label: 'Down', action: 'moveDown' }
        ]
    },
    {
        id: 'undoRedo',
        items: [
            { col: 0, label: 'Undo', action: 'undo' },
            { col: 1, label: 'Redo', action: 'redo' }
        ]
    },
    {
        id: 'clipboard',
        items: [
            { col: 0, label: 'Cut', action: 'cut' },
            { col: 1, label: 'Copy', action: 'copy' },
            { col: 2, label: 'Paste', action: 'paste' },
            // Select: ganzen Absatz (text/Main) bzw. ganzen Zuordnungsbereich
            // (note/Outline) selektieren — identisch zum Doppelklick auf den
            // >-Absatzmarker. Nur in text/note sinnvoll (visibleIn).
            { col: 3, label: 'Select', action: 'select', visibleIn: ['text', 'note'] },
            { col: 4, label: 'Delete', action: 'delete' }
        ]
    },
    {
        id: 'inlineFormats',
        items: [
            { col: 0, label: 'Bold', action: 'bold', previewStyle: Style.MENU_ITEM_BOLD },
            { col: 1, label: 'Italic', action: 'italic', previewStyle: Style.MENU_ITEM_ITALIC },
            { col: 2, label: 'Under', action: 'underline', previewStyle: Style.MENU_ITEM_UNDERLINE },
            { col: 3, label: 'Strike', action: 'strikethrough', previewStyle: Style.MENU_ITEM_STRIKE },
            // Inline-Code: 5. Inline-Format (existiert seit F1/F1b als
            // `toggleCode()` / `applyFormat('code')`, fehlte nur im Menue).
            { col: 4, label: 'Code', action: 'code', previewStyle: Style.MENU_ITEM_CODE }
        ]
    }
]

// Sichtbare Gruppen pro Kontext
const VISIBILITY = {
    text:      ['navigation', 'blockFormats', 'structure', 'indentMove', 'undoRedo', 'clipboard', 'inlineFormats'],
    note:      ['navigation', 'structure', 'indentMove', 'undoRedo', 'clipboard', 'inlineFormats'],
    info:      ['navigation', 'structure', 'indentMove', 'undoRedo', 'clipboard', 'inlineFormats'],
    separator: ['navigation', 'structure', 'indentMove', 'undoRedo', 'clipboard'],
    console:   ['undoRedo', 'clipboard', 'inlineFormats'],
    // Memory-Editor (Spec §10): keine navigation/structure (keine Sections)
    memory:    ['blockFormats', 'indentMove', 'undoRedo', 'clipboard', 'inlineFormats'],
    // Personal-Dict-Editor (Spellcheck §10 Phase 4): reine Wortliste
    personal_dict: ['undoRedo', 'clipboard']
}

// Layout-Konstanten
const MAX_COLS = 5          // Grid-Architektur ist 5-spaltig (leere Spalten = Breite 0)
const GUTTER_WIDTH = 2      // Mono-Gutter: 2 Cells (Praefix+Space oder kompakt '1.' oder leer)
// Trailing-Padding pro Spalte (Mono), in Cells. Visuelle Feinjustierung
// 2026-05-15: Nur Spalte 1 hat 1 Cell Padding, die uebrigen kein Padding.
const COL_PAD_CHARS_BY_COL = [0, 1, 0, 0, 0]
// Trailing-Padding pro Spalte (Prop) in Cell-Widths (Multiplikator fuer
// charWidth). 2026-05-15: Col 0,1,4 haben 1 Cell Pad; Col 2,3 keinen Pad —
// dadurch ruecken Col 3 um ~1 Cell und Col 4 um ~2 Cells nach links.
const COL_PAD_CELLS_PROP_BY_COL = [1, 1, 0, 0, 1]

// Actions die immer ausgegraut sind (heute leer — Fold/Unfold sind voll implementiert)
const ALWAYS_GRAYED = new Set()

// Hover-Varianten der Preview-Styles. Beim Hover bekommt das Label die
// Hover-Farbe, behaelt aber das Format-Flag (bold/italic/underline/strike).
// So bleibt das Preview als Aktions-Vorschau lesbar — der Hover signalisiert
// nur die Auswahl, nicht den Format-Verlust.
const HOVER_PREVIEW_STYLE = {
    [Style.MENU_ITEM_BOLD]: Style.MENU_ITEM_BOLD_HOVER,
    [Style.MENU_ITEM_ITALIC]: Style.MENU_ITEM_ITALIC_HOVER,
    [Style.MENU_ITEM_UNDERLINE]: Style.MENU_ITEM_UNDERLINE_HOVER,
    [Style.MENU_ITEM_STRIKE]: Style.MENU_ITEM_STRIKE_HOVER,
    [Style.MENU_ITEM_CODE]: Style.MENU_ITEM_CODE_HOVER
}

// Grayed-Varianten der Preview-Styles. Damit bleibt das Format-Preview
// auch bei ausgegrauten Items lesbar (gedaempfte Farbe, Format-Flag aktiv).
const GRAY_PREVIEW_STYLE = {
    [Style.MENU_ITEM_BOLD]: Style.MENU_ITEM_BOLD_GRAY,
    [Style.MENU_ITEM_ITALIC]: Style.MENU_ITEM_ITALIC_GRAY,
    [Style.MENU_ITEM_UNDERLINE]: Style.MENU_ITEM_UNDERLINE_GRAY,
    [Style.MENU_ITEM_STRIKE]: Style.MENU_ITEM_STRIKE_GRAY,
    [Style.MENU_ITEM_CODE]: Style.MENU_ITEM_CODE_GRAY
}

/**
 * Liefert den Gutter-String (genau GUTTER_WIDTH Zeichen).
 * - kein Praefix → '  '
 * - prefixCompact → der Praefix selbst (muss exakt GUTTER_WIDTH lang sein)
 * - sonst → Praefix + Space-Padding bis GUTTER_WIDTH
 */
function _gutterString(prefix, prefixCompact) {
    if (!prefix) return ' '.repeat(GUTTER_WIDTH)
    if (prefixCompact) return prefix.padEnd(GUTTER_WIDTH, ' ')
    return prefix.padEnd(GUTTER_WIDTH, ' ')
}

/**
 * Context Menu Klasse — kontextsensitives Menue im Overlay.
 */
export class ContextMenu {
    /**
     * @param {import('./overlay.js').Overlay} overlay
     */
    constructor(overlay) {
        this._overlay = overlay
        this._isOpen = false

        this._menuX = 0
        this._menuY = 0
        this._menuWidth = 0
        this._menuHeight = 0

        this._cells = []
        this._hoveredCell = -1
        this._proportionalInfo = null

        // Pro-Spalten-Breiten — werden in open() berechnet.
        this._colWidthsChars = new Array(MAX_COLS).fill(0)  // Mono: Chars pro Spalte (0 = Spalte leer)
        this._colPxWidths = new Array(MAX_COLS).fill(0)     // Prop: Pixel pro Spalte
        this._colPxStart = new Array(MAX_COLS).fill(0)      // Prop: Pixel-Offset ab menuX+1
    }

    /**
     * Oeffnet das Context Menu.
     *
     * @param {number} charX - Klick-Position (Character-Spalte)
     * @param {number} charY - Klick-Position (Character-Zeile)
     * @param {object} context - Kontext-Informationen
     * @param {object|null} proportionalInfo - { metricsCache, scaleFactor, charWidth } oder null
     */
    open(charX, charY, context, proportionalInfo) {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen
        blurKeyboardSink()
        const contextKey = context.isSeparator ? 'separator' : context.field
        const visibleGroupIds = VISIBILITY[contextKey] || VISIBILITY.text
        let visibleGroups = MENU_GROUPS.filter(g => visibleGroupIds.includes(g.id))

        // Console-Output-Selection: nur die Clipboard-Gruppe (read-only).
        if (context.isConsoleOutput) {
            visibleGroups = MENU_GROUPS.filter(g => g.id === 'clipboard')
        }

        // Cells aufbauen
        this._cells = []
        let row = 0
        for (const group of visibleGroups) {
            for (const item of group.items) {
                // Item-Level-Sichtbarkeit (z.B. @Link nur in text/note/info).
                // Items ohne `visibleIn` sind ueberall sichtbar, in denen
                // ihre Gruppe sichtbar ist.
                if (item.visibleIn && !item.visibleIn.includes(contextKey)) continue
                this._cells.push({
                    row,
                    col: item.col,
                    prefix: item.prefix || null,
                    prefixCompact: !!item.prefixCompact,
                    // Circular-Icon-Symbol fuer den Prop-Mode (Mono nutzt
                    // weiterhin den Text-Praefix).
                    iconSymbol: item.iconSymbol || null,
                    label: item.label,
                    action: item.action,
                    isGrayed: this._isItemGrayed(item.action, context),
                    previewStyle: item.previewStyle || null,
                    // Wird nach Positionsberechnung gesetzt
                    renderY: 0,
                    // Cell-Geometrie (Char-Koordinaten, absolut)
                    cellX: 0,
                    cellWidthChars: 0,
                    // Pixel-Geometrie nur im Prop-Mode benoetigt
                    cellPxStart: 0,
                    cellPxWidth: 0,
                    // Hit-Test-Bereich (kompatibel mit den alten Feld-Namen)
                    x1: 0,
                    x2: 0
                })
            }
            row++
        }

        const contentHeight = row
        this._menuHeight = contentHeight + 2  // +2 fuer Rahmen
        this._proportionalInfo = proportionalInfo

        // Spaltenbreiten + Menuebreite berechnen
        if (proportionalInfo) {
            this._computeProportionalLayout(proportionalInfo)
        } else {
            this._computeMonoLayout()
        }

        // Position berechnen
        const pos = this._calculatePosition(
            charX, charY,
            this._menuWidth, this._menuHeight,
            context.cols, context.rows
        )
        this._menuX = pos.x
        this._menuY = pos.y

        // Pro Cell renderY + cellX + Hit-Test-Bereich setzen
        for (const cell of this._cells) {
            cell.renderY = this._menuY + 1 + cell.row
            if (proportionalInfo) {
                const pxStart = this._colPxStart[cell.col]
                const pxWidth = this._colPxWidths[cell.col]
                cell.cellPxStart = pxStart
                cell.cellPxWidth = pxWidth
                const charWidth = proportionalInfo.charWidth
                const colStartChars = Math.floor(pxStart / charWidth)
                const colEndChars = Math.ceil((pxStart + pxWidth) / charWidth)
                cell.cellX = this._menuX + 1 + colStartChars
                cell.cellWidthChars = Math.max(1, colEndChars - colStartChars)
            } else {
                let offset = 0
                for (let i = 0; i < cell.col; i++) offset += this._colWidthsChars[i]
                cell.cellX = this._menuX + 1 + offset
                cell.cellWidthChars = this._colWidthsChars[cell.col]
            }
            cell.x1 = cell.cellX
            cell.x2 = cell.cellX + cell.cellWidthChars - 1
        }

        this._hoveredCell = -1
        this._isOpen = true
        this._render()
    }

    /** Schliesst das Menue. */
    close() {
        this._isOpen = false
        this._cells = []
        this._hoveredCell = -1
        this._overlay.deactivate()
    }

    /** @returns {boolean} */
    get isOpen() {
        return this._isOpen
    }

    /**
     * Verarbeitet einen Mausklick.
     */
    handleClick(charX, charY) {
        if (!this._isOpen) return 'outside'
        if (!this._isInsideMenu(charX, charY)) return 'outside'

        const cellIndex = this._findCell(charX, charY)
        if (cellIndex === -1) return null  // Rahmen oder leere Zelle

        const cell = this._cells[cellIndex]
        if (cell.isGrayed) return null

        return { action: cell.action }
    }

    /**
     * Verarbeitet Maus-Bewegung (Hover).
     * @returns {boolean} true wenn Redraw noetig
     */
    handleMouseMove(charX, charY) {
        if (!this._isOpen) return false

        let newHovered = -1
        if (this._isInsideMenu(charX, charY)) {
            const cellIndex = this._findCell(charX, charY)
            if (cellIndex !== -1 && !this._cells[cellIndex].isGrayed) {
                newHovered = cellIndex
            }
        }

        if (newHovered === this._hoveredCell) return false

        this._hoveredCell = newHovered
        this._render()
        return true
    }

    // === Private Methoden ===

    /**
     * Bestimmt ob ein Item ausgegraut sein soll.
     */
    _isItemGrayed(action, context) {
        if (ALWAYS_GRAYED.has(action)) return true

        // Console-Output-Selection: read-only — nur Copy aktiv.
        if (context.isConsoleOutput) {
            return action !== 'copy'
        }

        // Gesperrter Abschnitt: modifizierende Aktionen ausgrauen.
        // Lock bleibt aktiv, damit der User entsperren kann.
        if (context.isLocked) {
            switch (action) {
                case 'back':
                case 'forward':
                case 'copy':
                case 'undo':
                case 'redo':
                case 'lock':
                    break  // bleiben aktiv
                default:
                    return true
            }
        }

        // Collapse-Host: reduziertes Menue. Lock bleibt aktiv — Lock-State
        // ist orthogonal zum Collapse-State, beides muss umschaltbar sein.
        if (context.isCollapseHost) {
            switch (action) {
                case 'back':
                case 'forward':
                case 'copy':
                case 'undo':
                case 'redo':
                case 'unfold':
                case 'moveUp':
                case 'moveDown':
                case 'lock':
                    break
                default:
                    return true
            }
        }

        switch (action) {
            case 'back': return !context.hasHistoryBack
            case 'forward': return !context.hasHistoryForward
            case 'undo': return !context.canUndo
            case 'redo': return !context.canRedo
            case 'cut': return !context.hasSelection
            case 'copy': return !context.hasSelection
            case 'paste': return !context.hasClipboard
            case 'delete': return context.field === 'console' && !context.hasSelection
            case 'outdent': return !context.canOutdent
            case 'indent': return !context.canIndent
            case 'moveUp': return !context.canMoveUp
            case 'moveDown': return !context.canMoveDown
            case 'fold': return !context.canFold
            case 'unfold': return !context.canUnfold
            case 'link': return !context.hasLinkTargets
            // Lock im Background-Section deaktiviert (Story-Bible, outlineDepth=-1)
            case 'lock': return !!context.isBackground
            // 'plain' und 'code' sind reine Toggles ohne Vorbedingung — kein extra Grayed.
            default: return false
        }
    }

    /**
     * Berechnet die Menue-Position (5-stufiger Algorithmus).
     */
    _calculatePosition(clickX, clickY, width, height, cols, rows) {
        let x = clickX + 1
        let y = clickY - height
        if (y >= 0 && x + width <= cols) {
            return this._clampPosition(x, y, width, height, cols, rows)
        }
        x = clickX + 2
        y = clickY - Math.floor(height / 2)
        if (x + width <= cols) {
            return this._clampPosition(x, y, width, height, cols, rows)
        }
        x = clickX - width - 1
        y = clickY - Math.floor(height / 2)
        if (x >= 0) {
            return this._clampPosition(x, y, width, height, cols, rows)
        }
        x = clickX + 1
        y = clickY + 1
        if (y + height <= rows) {
            return this._clampPosition(x, y, width, height, cols, rows)
        }
        return this._clampPosition(x, y, width, height, cols, rows)
    }

    _clampPosition(x, y, width, height, cols, rows) {
        return {
            x: Math.max(0, Math.min(x, cols - width)),
            y: Math.max(0, Math.min(y, rows - height))
        }
    }

    _isInsideMenu(charX, charY) {
        return charX >= this._menuX && charX < this._menuX + this._menuWidth &&
               charY >= this._menuY && charY < this._menuY + this._menuHeight
    }

    /**
     * Findet die Zelle an einer Character-Position.
     * @returns {number} Index in _cells oder -1
     */
    _findCell(charX, charY) {
        for (let i = 0; i < this._cells.length; i++) {
            const cell = this._cells[i]
            if (charY !== cell.renderY) continue
            if (charX >= cell.cellX && charX < cell.cellX + cell.cellWidthChars) {
                return i
            }
        }
        return -1
    }

    /**
     * Berechnet die Mono-Spaltenbreiten + Menuebreite.
     * Pro Spalte: width = label_max + GUTTER + COL_PAD. Leere Spalten = 0.
     */
    _computeMonoLayout() {
        this._colWidthsChars = new Array(MAX_COLS).fill(0)
        for (const cell of this._cells) {
            const labelLen = cell.label ? cell.label.length : 0
            if (labelLen > this._colWidthsChars[cell.col]) {
                this._colWidthsChars[cell.col] = labelLen
            }
        }
        for (let i = 0; i < MAX_COLS; i++) {
            if (this._colWidthsChars[i] > 0) {
                this._colWidthsChars[i] += GUTTER_WIDTH + COL_PAD_CHARS_BY_COL[i]
            }
        }
        const inner = this._colWidthsChars.reduce((a, b) => a + b, 0)
        this._menuWidth = inner + 2  // +2 fuer Rahmen
    }

    /**
     * Berechnet die Prop-Spaltenbreiten (Pixel) + Menuebreite (Cols) und
     * cached die per-Cell Prefix-Px-Breite an `cell.prefixPxWidth`. Der
     * Render-Pfad liest die Prefix-Px-Breite direkt aus — `computeStringLayout`
     * fuer jeden Prefix in jedem Hover-Frame neu zu rufen war ein Hotspot
     * bei vielen sichtbaren Cells.
     */
    _computeProportionalLayout(pi) {
        const { metricsCache, scaleFactor, charWidth } = pi
        const baseStep = metricsCache.baseStep
        const gutterPx = GUTTER_WIDTH * charWidth
        const _runsFor = (s) => [{
            start: 0, end: s.length,
            bold: false, italic: false, underline: false, strikethrough: false
        }]

        const labelPxByCol = new Array(MAX_COLS).fill(0)
        const colHasItems = new Array(MAX_COLS).fill(false)
        for (const cell of this._cells) {
            colHasItems[cell.col] = true
            const labelPx = computeStringLayout(
                cell.label, metricsCache, baseStep, _runsFor(cell.label)
            ).totalWidth * scaleFactor
            if (labelPx > labelPxByCol[cell.col]) labelPxByCol[cell.col] = labelPx
            // Prefix-Px-Breite einmalig fuer den Render-Pfad cachen
            // (nicht-leere Text-Praefixe ohne Icon — Icons werden separat
            // pixel-zentriert ohne Layout-Lookup).
            cell.prefixPxWidth = (!cell.iconSymbol && cell.prefix)
                ? computeStringLayout(
                    cell.prefix, metricsCache, baseStep, _runsFor(cell.prefix)
                ).totalWidth * scaleFactor
                : 0
        }

        this._colPxWidths = labelPxByCol.map((px, i) =>
            colHasItems[i] ? px + gutterPx + COL_PAD_CELLS_PROP_BY_COL[i] * charWidth : 0
        )
        this._colPxStart = new Array(MAX_COLS)
        let cum = 0
        for (let i = 0; i < MAX_COLS; i++) {
            this._colPxStart[i] = cum
            cum += this._colPxWidths[i]
        }
        this._menuWidth = Math.ceil(cum / charWidth) + 2
    }

    /**
     * Rendert das Menue in den Overlay.
     */
    _render() {
        const overlay = this._overlay
        const pi = this._proportionalInfo
        overlay.clear()

        // Frame via Phase-1-Primitive — saubere Pixel-Geometrie.
        overlay.drawMenuFrame(
            { x: this._menuX, y: this._menuY, width: this._menuWidth, height: this._menuHeight },
            { borderStyle: Style.MENU_BORDER, bgStyle: Style.MENU_BACKGROUND }
        )

        for (let i = 0; i < this._cells.length; i++) {
            const cell = this._cells[i]
            const isHovered = i === this._hoveredCell

            // Style-Hierarchie:
            //  - grayed + previewStyle → MENU_ITEM_*_GRAY (gedaempfte Farbe,
            //    Format-Flag erhalten — auch ein ausgegrautes Bold-Item
            //    zeigt sich als Bold-Preview)
            //  - grayed ohne previewStyle → PANE_INFO (reine Dimm-Farbe)
            //  - Hover + previewStyle → MENU_ITEM_*_HOVER (Hover-Farbe +
            //    Format-Flag erhalten)
            //  - Hover ohne previewStyle → MENU_ITEM_HOVER (reine Hover-Farbe)
            //  - sonst previewStyle bzw. MENU_ITEM
            let style
            if (cell.isGrayed) {
                style = (cell.previewStyle && GRAY_PREVIEW_STYLE[cell.previewStyle])
                    || Style.PANE_INFO
            } else if (isHovered) {
                style = (cell.previewStyle && HOVER_PREVIEW_STYLE[cell.previewStyle])
                    || Style.MENU_ITEM_HOVER
            } else if (cell.previewStyle) {
                style = cell.previewStyle
            } else {
                style = Style.MENU_ITEM
            }

            // Praefix-Style faerbt sich bei Hover synchron mit dem Label (Q7).
            // Ohne Hover: neutraler Item-Style (kein previewStyle, damit
            // Underline/Strike nicht in den Gutter leaken).
            const prefixStyle = cell.isGrayed
                ? Style.PANE_INFO
                : (isHovered ? Style.MENU_ITEM_HOVER : Style.MENU_ITEM)

            // Stripe: vollflaechiger Hintergrund-Streifen ueber die Zellbreite.
            // Renderer malt Stripes als eigene Layer vor Cells/Spans, darum
            // ueberlagern Text und Praefix den Stripe sauber.
            if (isHovered) {
                overlay.addItemStripe(cell.cellX, cell.renderY, cell.cellWidthChars)
            }

            const gutter = _gutterString(cell.prefix, cell.prefixCompact)

            if (pi) {
                // Proportional: Gutter + Label als zwei pixel-genau platzierte Spans.
                // Die Fluchtlinie sitzt exakt an cellPxStart + GUTTER_WIDTH*charWidth.
                const cellPxAbs = (this._menuX + 1) * pi.charWidth + cell.cellPxStart
                const gutterPxWidth = GUTTER_WIDTH * pi.charWidth

                const labelPxAbs = cellPxAbs + gutterPxWidth
                const labelGridX = Math.floor(labelPxAbs / pi.charWidth)
                const labelFineOffset = labelPxAbs - labelGridX * pi.charWidth
                const maxCellCols = Math.ceil(cell.cellPxWidth / pi.charWidth) + 1

                // Gutter-Inhalt: entweder ein Circular-Icon (Fold/Unfold/Break/
                // Out/In/Lock) oder ein zentrierter Text-Praefix (#, •, 1., >, …)
                // oder gar nichts (Inline-Formate ohne Praefix).
                if (cell.iconSymbol) {
                    // Icon pixel-praezise in der Gutter-Mitte platzieren.
                    const iconCenterPx = cellPxAbs + gutterPxWidth / 2
                    overlay.addControlIcon(
                        iconCenterPx, cell.renderY,
                        cell.iconSymbol, prefixStyle
                    )
                } else if (cell.prefix) {
                    // Text-Praefix zentriert im 2-Cell-Gutter (siehe Fix
                    // 2026-05-15: Bullet '•' mittig unter dem Pfeil von 'Fwd').
                    // Nur das Glyph selbst rendern — kein Padding-Space im
                    // previewStyle, damit Under/Strike nicht in den Gutter leaken.
                    // prefixPxWidth ist im Layout-Pass `_computeProportionalLayout`
                    // einmalig gecached (sonst pro Hover-Frame neu berechnet).
                    const prefixStr = cell.prefix
                    const prefixPx = cell.prefixPxWidth || 0
                    const centerOffsetPx = Math.max(0, (gutterPxWidth - prefixPx) / 2)
                    const prefixPxAbs = cellPxAbs + centerOffsetPx
                    const prefixGridX = Math.floor(prefixPxAbs / pi.charWidth)
                    const prefixFineOffset = prefixPxAbs - prefixGridX * pi.charWidth

                    overlay.addTextSpan(
                        prefixGridX, cell.renderY,
                        prefixStr, prefixStyle,
                        GUTTER_WIDTH + 1,
                        { pixelOffsetX: prefixFineOffset }
                    )
                }

                // Label an der Fluchtlinie (mit previewStyle bei Inline-Formaten,
                // ueberschrieben durch MENU_ITEM_HOVER beim Hover).
                overlay.addTextSpan(
                    labelGridX, cell.renderY,
                    cell.label, style,
                    maxCellCols,
                    { pixelOffsetX: labelFineOffset }
                )
            } else {
                // Mono: Gutter + Label mit demselben Style. Bei Hover: Stripe
                // (siehe oben) + MENU_ITEM_HOVER-Farbe; kein Bold-Switch mehr,
                // damit der Text nicht springt.
                overlay.addTextSpan(cell.cellX, cell.renderY, gutter, prefixStyle, GUTTER_WIDTH)
                overlay.addTextSpan(
                    cell.cellX + GUTTER_WIDTH, cell.renderY,
                    cell.label, style,
                    cell.cellWidthChars - GUTTER_WIDTH
                )
            }
        }

        overlay.isDirty = true
    }
}

// Exporte fuer Tests
export { MENU_GROUPS, VISIBILITY, MAX_COLS, GUTTER_WIDTH }
